import { ReactNode, useId } from 'react';
import { Popover2025, PopoverPlacement } from './Popover2025';
import { StyledPopoverContainer } from './StyledPopoverContainer';
import { usePointAnchor, ViewportPoint } from './usePointAnchor';

type BasePopoverAtPointProps = {
  /** Viewport (client) coordinates to anchor the popover to. */
  point: ViewportPoint;
  isOpen: boolean;
  onClose: VoidFunction;
  onClickOutside?: VoidFunction;
  placement?: PopoverPlacement;
  children: ReactNode;
};

/**
 * The popover receives pointer events: it stays open while hovered, can hold
 * interactive content, and accepts mouse handlers.
 */
type InteractivePopoverAtPointProps = BasePopoverAtPointProps & {
  acceptsMouse: true;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
};

/**
 * The default: the popover is click-through, matching a coordinate popover that
 * just points at a spot. Mouse handlers are disallowed because
 * `pointer-events: none` would make them dead — set `acceptsMouse` to use them.
 */
type ClickThroughPopoverAtPointProps = BasePopoverAtPointProps & {
  acceptsMouse?: false;
};

export type PopoverAtPointProps =
  | InteractivePopoverAtPointProps
  | ClickThroughPopoverAtPointProps;

/**
 * Renders a popover anchored to a point in the viewport rather than to a DOM
 * element. Use for imperative "show a popover at these coordinates" cases
 * (canvas/graph views) where there is no element to anchor to.
 */
export const PopoverAtPoint = (props: PopoverAtPointProps) => {
  const {
    point,
    isOpen,
    onClose,
    onClickOutside,
    placement = { side: 'bottom', overflow: 'center' },
    children,
  } = props;
  const acceptsMouse = props.acceptsMouse === true;
  const onMouseEnter = props.acceptsMouse ? props.onMouseEnter : undefined;
  const onMouseLeave = props.acceptsMouse ? props.onMouseLeave : undefined;
  const id = useId();
  const { anchorRef, anchorPortal } = usePointAnchor(point);

  return (
    <>
      {anchorPortal}
      <Popover2025
        isOpen={isOpen}
        onClose={onClose}
        onClickOutside={onClickOutside}
        id={id}
        anchorRef={anchorRef}
        placement={placement}
        forcePortal
      >
        <StyledPopoverContainer
          isOpen={isOpen}
          preferredSide={placement.side}
          anchorId={id}
          anchorRef={anchorRef}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          disablePointerEvents={!acceptsMouse}
          trackAnchor
        >
          {children}
        </StyledPopoverContainer>
      </Popover2025>
    </>
  );
};
